import { Enums } from '@cornerstonejs/core';
import classnames from 'classnames';
import React from 'react';
import { useDrag } from 'react-dnd';

import { SidebarPosition } from '@core/types';
import Icon from '../Icon';
import IconButton from '../IconButton';
import ProgressLoadingBar from '../ProgressLoadingBar';
import Tooltip from '../Tooltip';

const getOrientationShortLabel = (orientation: string): string => {
	switch (orientation) {
		case Enums.OrientationAxis.AXIAL:
		case Enums.OrientationAxis.AXIAL_REFORMAT:
			return 'A';
		case Enums.OrientationAxis.SAGITTAL:
		case Enums.OrientationAxis.SAGITTAL_REFORMAT:
			return 'S';
		case Enums.OrientationAxis.CORONAL:
		case Enums.OrientationAxis.CORONAL_REFORMAT:
			return 'C';
		case Enums.OrientationAxis.ACQUISITION:
			return 'Q';
		default:
			return '';
	}
};

export interface DragDataType {
	/** Must match the "type" a dropTarget expects */
	type: string;
	[key: string]: unknown;
}

interface ThumbnailProps {
	displaySetInstanceUID: string;
	className?: string;
	imageSrc?: string;
	imageAltText?: string;
	description: string;
	seriesNumber: string | number;
	numInstances: number;
	messages?: {
		messages: {
			id: string;
		}[];
	};
	isActive: boolean;
	onClick: () => void;
	onDoubleClick: () => void;
	orientation?: string;
	viewportIdentificator?: string[];
	loadingProgress?: number;
	/**
	 * Data the thumbnail should expose to a receiving drop target. Use a matching
	 * `dragData.type` to identify which targets can receive this draggable item.
	 * If this is not set, drag-n-drop will be disabled for this thumbnail.
	 *
	 * Ref: https://react-dnd.github.io/react-dnd/docs/api/use-drag#specification-object-members
	 */
	dragData?: DragDataType;

	isSubSeries?: boolean;
	SubSeriesIndex?: number;
	collapsible?: boolean;
	collapsed?: boolean;
	onToggleCollapse?: (e: React.MouseEvent<HTMLButtonElement>) => void;
	seriesInstanceUID?: string;
	sidePosition?: SidebarPosition;
}

/**
 * Display a thumbnail for a display set.
 */
const Thumbnail = ({
	displaySetInstanceUID,
	className,
	imageSrc,
	// imageAltText,
	description,
	seriesNumber,
	numInstances,
	orientation,
	// messages,
	dragData,
	isActive,
	onClick,
	onDoubleClick,
	loadingProgress,
	isSubSeries = false,
	collapsible = false,
	collapsed = false,
	onToggleCollapse,
	seriesInstanceUID,
	sidePosition,
}: ThumbnailProps): React.ReactElement => {
	// TODO: We should wrap our thumbnail to create a "DraggableThumbnail", as
	// this will still allow for "drag", even if there is no drop target for the
	// specified item.
	const [_collectedProps, drag, _dragPreview] = useDrag({
		type: 'displayset',
		item: {
			...dragData,
		},
		canDrag: function (_monitor) {
			return Object.keys(dragData).length !== 0;
		},
	});

	const style = imageSrc
		? {
				height: `120px`,
				width: `100px`,
				backgroundImage: `url("${imageSrc}")`,
				backgroundRepeat: `no-repeat`,
				backgroundSize: 'contain',
				backgroundPosition: `center center`,
			}
		: {
				height: `120px`,
				width: `100px`,
			};

	return (
		<div
			className={classnames(
				className,
				'group flex w-full flex-1 cursor-pointer select-none flex-col outline-none',
				isActive
					? 'border border-primary-light text-primary-light'
					: 'border border-common-dark text-common-light hover:border-blue-300',
				collapsed && isSubSeries ? 'hidden' : '',
			)}
			id={`thumbnail-${displaySetInstanceUID}`}
			data-cy={`study-browser-thumbnail`}
			onClick={onClick}
			onDoubleClick={onDoubleClick}
			role="button"
			tabIndex={0}
			style={style}
		>
			<Tooltip content={description} position={sidePosition === SidebarPosition.LEFT ? 'right' : 'left'} className="h-full w-full">
				<div ref={drag} className="flex h-full w-full flex-1 flex-col justify-between">
					<div className="w-24 truncate text-center text-xs">
						{`${orientation ? `(${getOrientationShortLabel(orientation)}) ` : ''}${description}`}
					</div>
					{/* <div
					className={classnames('flex flex-1 items-center justify-center overflow-hidden rounded-md bg-black text-xs ')}
					style={{
						margin: isActive ? '0' : '1px',
						width: `100%`
					}}
				>
					{imageSrc ? (
						<img
							src={imageSrc}
							alt={imageAltText}
							className="object-none"
							crossOrigin="anonymous"
						/>
					) : (
						<div>{imageAltText}</div>
					)}
				</div> */}
					<div className="flex flex-col">
						{collapsible && !isSubSeries && (
							<IconButton
								id={`toggle-collapse-${displaySetInstanceUID}`}
								onClick={onToggleCollapse}
								data-series-instance-uid={seriesInstanceUID}
								data-collapsed={collapsed}
								size="small"
								className="w-[24px] h-[24px] m-[1px] rounded-sm bg-primary-main text-common-bright hover:bg-primary-light hover:text-white  focus:bg-primary-light focus:text-white"
							>
								<Icon name={collapsed ? 'arrow-right-small' : 'arrow-down-small'} />
							</IconButton>
						)}
						<ProgressLoadingBar progress={loadingProgress * 100} className="mx-1 h-[2px] w-auto rounded-none bg-transparent" />
						<div className="flex flex-row items-center text-xs">
							<div className="ml-1 flex flex-1 flex-row items-center">
								{/* <Icon
							name={countIcon || 'group-layers'}
							className="mr-1 w-3"
						/> */}
								{`${numInstances}`}
							</div>
							<div className="mr-1">
								<span>{'Ser: '}</span>
								{seriesNumber}
							</div>
							{/* <DisplaySetMessageListTooltip
						messages={messages}
						id={`display-set-tooltip-${displaySetInstanceUID}`}
					/> */}
						</div>
					</div>
				</div>
			</Tooltip>
		</div>
	);
};

export default Thumbnail;
